import type { ConfirmModalProps } from '~/features/common';
import { Button, Stack, TextField, useDialog } from '@workramp/ui';
import { ConfirmModal, EmptyTableState } from '~/features/common';
import { I18n } from '~/common/explicit-exports';
import { hideIntercomButton, showIntercomButton } from '~/utils';
import { WebhookIcon } from '../../icons';
import { IntegrationContainer } from '../../styles';
import { IntegrationHeader } from '../IntegrationHeader';
import { WebhookSubscriptionDrawer } from '../WebhookSubscriptionDrawer';
import { WebhookSubscriptionCard } from '../WebhookSubscriptionCard';
import { WebhookTestModal } from '../WebhookTestModal';

interface WebhookConfirmModalProps extends Omit<ConfirmModalProps, 'onCancel' | 'onConfirm' | 'title'> {
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  isOpen: boolean;
}

interface WebhookSubscriptionsIntegrationPageProps {
  deprecatedEventTypes?: string[];
  integrationBaseUrl: string;
  webhookSubscriptionSigningSecret?: string;
  webhookEventTypes: string[];
  webhookSubscriptions: {
    createdAt: number;
    deletedAt?: number;
    eventTypes: string[];
    id: string;
    name: string;
    updatedAt: number;
    lastUpdatedBy?: {
      name?: string;
      email: string;
    };
    url: string;
  }[];
  onCreate: (data: { name: string; url: string; eventTypes: string[] }, callbackFn?: () => void) => void;
  onUpdate: (data: { id: string; name?: string; url?: string; eventTypes?: string[] }, callbackFn?: () => void) => void;
  onDelete: (id: string, callbackFn?: () => void) => void;
  onTest: (data: { id: string; eventType: string }) => void;
}

const HEADER_NAME = I18n.t('js.admin.integration_settings.webhook_subscriptions.name');
const HEADER_DESCRIPTION = I18n.t('js.admin.integration_settings.webhook_subscriptions.description');
const TABLE_EMPTY_HEADING = I18n.t('js.admin.integration_settings.webhook_subscriptions.table_empty_heading');
const ADD_WEBHOOK = I18n.t('js.admin.integration_settings.webhook_subscriptions.add_webhook');
const EDIT_WEBHOOK = I18n.t('js.admin.integration_settings.webhook_subscriptions.edit_webhook');
const DELETE_CONFIRMATION = I18n.t('js.admin.integration_settings.webhook_subscriptions.delete_webhook_confirmation');
const DRAWER_ADD = I18n.t('js.admin.integration_settings.webhook_subscriptions.drawer.add');
const DELETE = I18n.t('js.admin.integration_settings.webhook_subscriptions.delete');

export const WebhookSubscriptionsIntegrationPageContent = ({
  deprecatedEventTypes,
  integrationBaseUrl,
  webhookSubscriptionSigningSecret,
  webhookEventTypes,
  webhookSubscriptions,
  onCreate,
  onUpdate,
  onDelete,
  onTest,
}: WebhookSubscriptionsIntegrationPageProps) => {
  const { open: openDeleteModal, close: closeDeleteModal } = useDialog<WebhookConfirmModalProps>(ConfirmModal);
  const { open: openWebhookSubscriptionDrawer, close: closeWebhookSubscriptionDrawer } =
    useDialog(WebhookSubscriptionDrawer);
  const { open: openTestModal, close: closeTestModal } = useDialog(WebhookTestModal);

  const handleCreate = () => {
    openWebhookSubscriptionDrawer({
      modalTitle: ADD_WEBHOOK,
      confirm: DRAWER_ADD,
      webhookEventTypeOptions: webhookEventTypes,
      name: '',
      url: '',
      eventTypes: [],
      deprecatedEventTypes,
      onClose: closeWebhookSubscriptionDrawer,
      onSave: (data) => {
        onCreate(data, closeWebhookSubscriptionDrawer);
      },
      onEnter: hideIntercomButton,
      onExit: showIntercomButton,
    });
  };

  const handleUpdate = ({
    id,
    name,
    url,
    eventTypes,
  }: {
    id: string;
    name: string;
    url: string;
    eventTypes: string[];
  }) => {
    openWebhookSubscriptionDrawer({
      modalTitle: EDIT_WEBHOOK,
      confirm: I18n.t('js.common.modal.save'),
      webhookEventTypeOptions: webhookEventTypes,
      deprecatedEventTypes,
      eventTypes,
      name,
      url,
      onClose: closeWebhookSubscriptionDrawer,
      onSave: (data) => {
        onUpdate({ id, ...data }, closeWebhookSubscriptionDrawer);
      },
      onEnter: hideIntercomButton,
      onExit: showIntercomButton,
    });
  };

  const handleDelete = (id: string) => {
    openDeleteModal({
      title: DELETE_CONFIRMATION,
      confirm: DELETE,
      onConfirm: () => {
        onDelete(id);
        closeDeleteModal();
      },
      onCancel: closeDeleteModal,
    });
  };

  const handleTestWebhook = (webhook) => {
    openTestModal({
      webhookSubscriptionUrl: webhook.url,
      webhookEventTypeOptions: webhookEventTypes,
      onClose: closeTestModal,
      onTestWebhook: (eventType) => {
        onTest({ id: webhook.id, eventType });
      },
    });
  };

  const actions = (
    <Button onPress={handleCreate} color="secondary" variant="outline" size="lg">
      {ADD_WEBHOOK}
    </Button>
  );

  return (
    <IntegrationContainer>
      <IntegrationHeader
        baseUrl={integrationBaseUrl}
        icon={<WebhookIcon />}
        name={HEADER_NAME}
        description={HEADER_DESCRIPTION}
        actions={actions}
      />
      <Stack size={8}>
        {webhookSubscriptionSigningSecret && (
          <TextField label="Signing Secret" value={webhookSubscriptionSigningSecret} isDisabled isReadOnly />
        )}
        {webhookSubscriptions.length > 0 ? (
          <Stack size={6}>
            {webhookSubscriptions.map((webhook) => (
              <WebhookSubscriptionCard
                key={webhook.id}
                name={webhook.name}
                url={webhook.url}
                eventTypes={webhook.eventTypes}
                updatedAt={webhook.updatedAt}
                lastUpdatedBy={webhook.lastUpdatedBy}
                onEdit={() => handleUpdate(webhook)}
                onDelete={() => handleDelete(webhook.id)}
                onTest={() => handleTestWebhook(webhook)}
              />
            ))}
          </Stack>
        ) : (
          <EmptyTableState heading={TABLE_EMPTY_HEADING} action={actions} />
        )}
      </Stack>
    </IntegrationContainer>
  );
};
